<script lang="ts" setup>
const { t } = useI18n()

const attributes: ApiAttribute[] = [
  {
    description: t('attributes.code'),
    example: '403',
    name: 'code',
    parent: 'error',
    type: 'string',
  },
  {
    description: t('attributes.message'),
    example: 'Missing API token',
    name: 'message',
    parent: 'error',
    type: 'string',
  },
  {
    description: t('attributes.type'),
    example: 'apiConnectionError',
    name: 'type',
    parent: 'error',
    type: 'string',
    values: [
      'apiConnectionError',
      'apiError',
      'authenticationError',
      'invalidRequestError',
      'noCreditsRemaining',
      'rateLimitError',
    ],
  },
]
</script>

<template>
  <ApiDoc>
    <template #left>
      <ApiAttributes
        :attributes="attributes"
        :title="t('errorObject')"
      />
    </template>

    <template #right>
      <ApiBox :title="t('sidebar.httpStatusCodeSummary.title')">
        <ApiBoxValue
          :label="t('sidebar.httpStatusCodeSummary.200.text')"
          :value="t('sidebar.httpStatusCodeSummary.200.value')"
        />

        <ApiBoxValue
          :label="t('sidebar.httpStatusCodeSummary.400.text')"
          :value="t('sidebar.httpStatusCodeSummary.400.value')"
        />

        <ApiBoxValue
          :label="t('sidebar.httpStatusCodeSummary.401.text')"
          :value="t('sidebar.httpStatusCodeSummary.401.value')"
        />

        <ApiBoxValue
          :label="t('sidebar.httpStatusCodeSummary.402.text')"
          :value="t('sidebar.httpStatusCodeSummary.402.value')"
        />

        <ApiBoxValue
          :label="t('sidebar.httpStatusCodeSummary.403.text')"
          :value="t('sidebar.httpStatusCodeSummary.403.value')"
        />

        <ApiBoxValue
          :label="t('sidebar.httpStatusCodeSummary.404.text')"
          :value="t('sidebar.httpStatusCodeSummary.404.value')"
        />

        <ApiBoxValue
          :label="t('sidebar.httpStatusCodeSummary.409.text')"
          :value="t('sidebar.httpStatusCodeSummary.409.value')"
        />

        <ApiBoxValue
          :label="t('sidebar.httpStatusCodeSummary.429.text')"
          :value="t('sidebar.httpStatusCodeSummary.429.value')"
        />

        <ApiBoxValue
          :label="t('sidebar.httpStatusCodeSummary.500.text')"
          :value="t('sidebar.httpStatusCodeSummary.500.value')"
        />
      </ApiBox>

      <ApiBox :title="t('sidebar.httpErrorsTypesSummary.title')">
        <ApiBoxValue
          label="apiConnectionError"
          :value="t('sidebar.httpErrorsTypesSummary.apiConnectionError')"
        />

        <ApiBoxValue
          label="apiError"
          :value="t('sidebar.httpErrorsTypesSummary.apiError')"
        />

        <ApiBoxValue
          label="authenticationError"
          :value="t('sidebar.httpErrorsTypesSummary.authenticationError')"
        />

        <ApiBoxValue
          label="invalidRequestError"
          :value="t('sidebar.httpErrorsTypesSummary.invalidRequestError')"
        />

        <ApiBoxValue
          label="noCreditsRemaining"
          :value="t('sidebar.httpErrorsTypesSummary.noCreditsRemaining')"
        />

        <ApiBoxValue
          label="rateLimitError"
          :value="t('sidebar.httpErrorsTypesSummary.rateLimitError')"
        />
      </ApiBox>
    </template>
  </ApiDoc>
</template>

<i18n lang="json">
{
  "en": {
    "attributes": {
      "code": "For some errors that could be handled programmatically, a number indicating the error code reported.",
      "message": "A message providing more details about the error.",
      "type": "The type of error returned (see the list on your right)."
    },
    "errorObject": "Error object",
    "sidebar": {
      "httpErrorsTypesSummary": {
        "apiConnectionError": "Failure to connect to The Companies API.",
        "apiError": "API errors cover any other type of problem (e.g., a temporary problem with The Companies API's servers) and are extremely uncommon.",
        "authenticationError": "Failure to properly authenticate yourself in the request.",
        "invalidRequestError": "Invalid request errors arise when your request has invalid parameters.",
        "noCreditsRemaining": "You have no remaining credits to make this request.",
        "rateLimitError": "Too many requests hit the API too quickly. We recommend an exponential backoff of your requests.",
        "title": "HTTP errors types summary"
      },
      "httpStatusCodeSummary": {
        "200": {
          "text": "200 - OK",
          "value": "The request was successful."
        },
        "400": {
          "text": "400 - Bad Request",
          "value": "The request was unacceptable, often due to missing a required parameter."
        },
        "401": {
          "text": "401 - Unauthorized",
          "value": "No valid API token provided."
        },
        "402": {
          "text": "402 - Request Failed",
          "value": "The parameters were valid, but the request failed."
        },
        "403": {
          "text": "403 - Forbidden",
          "value": "The API token doesn't have permission to perform the request."
        },
        "404": {
          "text": "404 - Not Found",
          "value": "The requested resource doesn't exist."
        },
        "409": {
          "text": "409 - Conflict",
          "value": "The request conflicts with another request (perhaps due to using the same idempotent key)."
        },
        "429": {
          "text": "429 - Too Many Requests",
          "value": "Too many requests hit the API too quickly. We recommend an exponential backoff of your requests."
        },
        "500": {
          "text": "500, 502, 503, 504 - Server Errors",
          "value": "Something went wrong on our end. These rare errors should be reported, but we catch them with PostHog and fix them as soon as they occur."
        },
        "title": "HTTP status code summary"
      }
    }
  },
  "es": {
    "attributes": {
      "code": "Para algunos errores que podrían manejarse programáticamente, un número que indica el código de error reportado.",
      "message": "Un mensaje que proporciona más detalles sobre el error.",
      "type": "El tipo de error devuelto (ver la lista a su derecha)."
    },
    "errorObject": "Objeto de error",
    "sidebar": {
      "httpErrorsTypesSummary": {
        "apiConnectionError": "Fallo al conectar con la API de Companies.",
        "apiError": "Los errores de API cubren cualquier otro tipo de problema (por ejemplo, un problema temporal con los servidores de la API de Companies) y son extremadamente poco comunes.",
        "authenticationError": "Fallo al autenticarse correctamente en la solicitud.",
        "invalidRequestError": "Los errores de solicitud inválida surgen cuando su solicitud tiene parámetros inválidos.",
        "noCreditsRemaining": "No tiene créditos restantes para hacer esta solicitud.",
        "rateLimitError": "Demasiadas solicitudes golpearon la API muy rápidamente. Recomendamos un retroceso exponencial de sus solicitudes.",
        "title": "Resumen de tipos de errores HTTP"
      },
      "httpStatusCodeSummary": {
        "200": {
          "text": "200 - OK",
          "value": "La solicitud fue exitosa."
        },
        "400": {
          "text": "400 - Solicitud Incorrecta",
          "value": "La solicitud fue inaceptable, a menudo debido a la falta de un parámetro requerido."
        },
        "401": {
          "text": "401 - No Autorizado",
          "value": "No se proporcionó un token de API válido."
        },
        "402": {
          "text": "402 - Solicitud Fallida",
          "value": "Los parámetros eran válidos, pero la solicitud falló."
        },
        "403": {
          "text": "403 - Prohibido",
          "value": "El token de API no tiene permiso para realizar la solicitud."
        },
        "404": {
          "text": "404 - No Encontrado",
          "value": "El recurso solicitado no existe."
        },
        "409": {
          "text": "409 - Conflicto",
          "value": "La solicitud entra en conflicto con otra solicitud (quizás debido al uso de la misma clave idempotente)."
        },
        "429": {
          "text": "429 - Demasiadas Solicitudes",
          "value": "Demasiadas solicitudes golpearon la API muy rápidamente. Recomendamos un retroceso exponencial de sus solicitudes."
        },
        "500": {
          "text": "500, 502, 503, 504 - Errores del Servidor",
          "value": "Algo salió mal en nuestro lado. Estos errores raros deben ser reportados, pero los capturamos con PostHog y los arreglamos tan pronto como ocurren."
        },
        "title": "Resumen de códigos de estado HTTP"
      }
    }
  },
  "fr": {
    "attributes": {
      "code": "Pour certaines erreurs qui peuvent être gérées de manière programmable, un chiffre indiquant le code d'erreur rapporté.",
      "message": "Un message lisible par un humain fournissant plus de détails sur l'erreur.",
      "type": "Le type d'erreur retourné (voir la liste à votre droite)."
    },
    "errorObject": "Objet d'erreur",
    "sidebar": {
      "httpErrorsTypesSummary": {
        "apiConnectionError": "Échec de la connexion à The Companies API.",
        "apiError": "Les erreurs API couvrent tout autre type de problème (par exemple, un problème temporaire avec les serveurs de The Companies API) et sont extrêmement rares.",
        "authenticationError": "Échec de l'authentification dans la requête.",
        "invalidRequestError": "Les erreurs de requête invalides se produisent lorsque votre requête a des paramètres invalides.",
        "noCreditsRemaining": "Vous n'avez plus de crédits restants pour effectuer cette requête.",
        "rateLimitError": "Trop de requêtes ont frappé l'API trop rapidement. Nous vous recommandons une temporisation de vos requêtes.",
        "title": "Résumé des types d'erreurs HTTP"
      },
      "httpStatusCodeSummary": {
        "200": {
          "text": "200 - OK",
          "value": "La requête a réussi."
        },
        "400": {
          "text": "400 - Bad Request",
          "value": "La requête est refusée, souvent en raison de l'absence d'un paramètre requis."
        },
        "401": {
          "text": "401 - Unauthorized",
          "value": "Aucun jeton API valide fourni."
        },
        "402": {
          "text": "402 - Request Failed",
          "value": "Les paramètres étaient valides, mais la requête a échoué."
        },
        "403": {
          "text": "403 - Forbidden",
          "value": "Le jeton API n'a pas la permission d'exécuter la requête."
        },
        "404": {
          "text": "404 - Not Found",
          "value": "La ressource demandée n'existe pas."
        },
        "409": {
          "text": "409 - Conflict",
          "value": "La requête est en conflit avec une autre requête (peut-être en raison de l'utilisation de la même clé idempotente)."
        },
        "429": {
          "text": "429 - Too Many Requests",
          "value": "Trop de requêtes ont frappé l'API trop rapidement. Nous vous recommandons une temporisation de vos requêtes."
        },
        "500": {
          "text": "500, 502, 503, 504 - Server Errors",
          "value": "Quelque chose s'est mal passé de notre côté. Ces rares erreurs doivent être signalées, mais nous les enregistrons avec PostHog et les corrigeons dès qu'elles se produisent."
        },
        "title": "Résumé des codes de statut HTTP"
      }
    }
  }
}
</i18n>
